(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.DIMSceneIdentity = api;
}(typeof globalThis !== 'undefined' ? globalThis : this, () => {
  'use strict';
  const exactKeys = (value, keys) => value && typeof value === 'object' && !Array.isArray(value)
    && Object.keys(value).sort().join(',') === [...keys].sort().join(',');
  const positive = value => Number.isSafeInteger(value) && value > 0;
  const hex = value => typeof value === 'string' && /^#[0-9A-F]{6}$/.test(value);
  const validateWordsCapability = (value) => {
    if (!exactKeys(value, ['schemaVersion', 'background', 'inkColours', 'mappingVersion', 'mappingChecksum'])
      || value.schemaVersion !== 'words-only-capability@1'
      || !exactKeys(value.background, ['kind', 'colour']) || value.background.kind !== 'solid' || !hex(value.background.colour)
      || !Array.isArray(value.inkColours) || !value.inkColours.length || value.inkColours.length > 16
      || value.inkColours.some(colour => !hex(colour)) || new Set(value.inkColours.map(colour => colour.toUpperCase())).size !== value.inkColours.length
      || value.inkColours.includes(value.background.colour)
      || !positive(value.mappingVersion) || !/^sha256:[a-f0-9]{64}$/.test(value.mappingChecksum)) {
      throw new Error('The Words-only capability is unavailable or invalid.');
    }
    return Object.freeze({ schemaVersion: value.schemaVersion,
      background: Object.freeze({ kind: 'solid', colour: value.background.colour.toUpperCase() }),
      inkColours: Object.freeze(value.inkColours.map(colour => colour.toUpperCase())),
      mappingVersion: value.mappingVersion, mappingChecksum: value.mappingChecksum });
  };
  function validateComposition(composition, options = {}) {
    if (composition === undefined) return null;
    const { originalImageKey = '', originalRevision = 0, now = Date.now(), allowExpired = false } = options;
    if (!exactKeys(composition, ['kind', 'revision', 'createdAt', 'expiresAt', 'capability']) || composition.kind !== 'words'
      || !positive(composition.revision) || !positive(composition.createdAt) || !positive(composition.expiresAt)
      || composition.expiresAt <= composition.createdAt || composition.expiresAt - composition.createdAt > 7200000
      || !Number.isFinite(now) || composition.createdAt > now) throw new Error('The Words-only composition or expiry is invalid.');
    if (!allowExpired && composition.expiresAt <= now) throw new Error('The Words-only composition has expired.');
    if (originalImageKey !== '' || originalRevision !== 0) throw new Error('Words-only composition must explicitly have no original image.');
    const capability = validateWordsCapability(composition.capability);
    if (Object.hasOwn(options, 'resolution')) {
      const admitted = validateWordsCapability(options.resolution?.words_only);
      if (JSON.stringify(capability) !== JSON.stringify(admitted)) throw new Error('The Words-only capability no longer matches this mapping.');
    }
    return Object.freeze({ kind: 'words', revision: composition.revision, createdAt: composition.createdAt,
      expiresAt: composition.expiresAt, capability });
  }
  function validateCompositionText(composition, layers, { forExport = false } = {}) {
    if (!composition) return true;
    if (!Array.isArray(layers)) throw new Error('The Words-only lettering is unavailable.');
    if (layers.some(layer => typeof layer?.text !== 'string'
      || (layer.text.trim() && !composition.capability.inkColours.includes(String(layer.colour).toUpperCase())))) {
      throw new Error('Use an admitted ink colour for this Words-only composition.');
    }
    if (forExport && (!layers.length || layers.some(layer => !layer.text.trim()))) {
      throw new Error('Add nonempty words before preparing this composition.');
    }
    return true;
  }
  // The persistent original revision identifies immutable bytes, not a page's
  // operation counter. All consumers use this adapter for prepared identity.
  function capture(runtime, draft, { originalRevision = draft.originalRevision, draftRevision = 0, editorRevision = 0 } = {}) {
    const composition = validateComposition(draft?.composition, { resolution: draft?.resolution,
      originalImageKey: draft?.originalImageKey, originalRevision });
    if (!runtime?.scene || draft?.version !== 7 || (!composition
      && (!Number.isSafeInteger(originalRevision) || originalRevision < 1 || !draft.originalImageKey))) {
      throw new Error('The original artwork identity is unavailable.');
    }
    const resolution = draft.resolution;
    return {
      runtimeScope: JSON.stringify([runtime.runtime.environment, runtime.runtime.instanceId,
        runtime.runtime.environment === 'development' ? runtime.runtime.themeId : null, runtime.runtime.workerOrigin]),
      recordScope: runtime.scene.keys.recordScope,
      context: runtime.context, draftRevision, editorRevision,
      productId: draft.productId, originalKey: draft.originalImageKey, originalRevision,
      ...(composition ? { composition } : {}),
      identity: {
        variantId: resolution?.shopify_variant_id, releaseId: resolution?.release_id,
        profileKey: resolution?.profile_key, profileVersion: resolution?.profile_version,
        profileContentChecksum: resolution?.profile_content_checksum, profileChecksum: resolution?.profile_checksum,
        outputWidth: resolution?.editor_output_size_px?.width, outputHeight: resolution?.editor_output_size_px?.height,
        initialPlacement: resolution?.editor_initial_placement || 'fit'
      },
      editorState: draft.editorState
    };
  }
  function matches(runtime, draft, record, printable) {
    try {
      return record.id === draft.imageKey && record.preparedScene?.blobRecordId === record.id
        && printable.isPreparedReusable(record.preparedScene, capture(runtime, draft));
    } catch (_) { return false; }
  }
  const fingerprintProviders = new WeakMap();
  async function fingerprint(blob, cryptoProvider = globalThis.crypto, timeoutMs = 120000, revision = 0) {
    if (!blob || typeof blob.arrayBuffer !== 'function' || !cryptoProvider?.subtle?.digest) throw new Error('Artwork verification is unavailable.');
    let records = fingerprintProviders.get(cryptoProvider);
    if (!records) { records = new WeakMap(); fingerprintProviders.set(cryptoProvider, records); }
    let entry = records.get(blob);
    if (!entry || entry.revision !== revision || (entry.completedAt && Date.now() - entry.completedAt >= 60000)) {
      entry = { revision, completedAt: 0 };
      records.set(blob, entry);
      // Blob bytes are immutable. Coalesce concurrent verification only for
      // this exact Blob/provider/revision; an ID or persisted digest is no proof.
      entry.promise = Promise.resolve().then(() => blob.arrayBuffer()).then(buffer => cryptoProvider.subtle.digest('SHA-256', buffer))
        .then(digest => {
          const value = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
          entry.completedAt = Date.now();
          return value;
        }, error => { if (records.get(blob) === entry) records.delete(blob); throw error; });
    }
    let timer;
    try {
      return await Promise.race([
        entry.promise,
        new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('Artwork verification timed out.')), timeoutMs); })
      ]);
    } finally { clearTimeout(timer); }
  }
  return Object.freeze({ capture, matches, fingerprint, validateWordsCapability, validateComposition, validateCompositionText });
}));
